import { useState, useEffect } from 'react';
import { useUserData } from 'Foundation/User/UserContext';

type SessionStoragePolicies = {
  data: any;
  timestamp: string;
};

export enum APIList {
  billing = 'billing',
  claims = 'claims',
  renewals = 'renewals',
  policies = 'policies',
}

const apis: { [key in APIList]: string } = {
  [APIList.billing]: 'api/sitecore/RecentActivitiesPL/GetRecentPLBillingActivities',
  [APIList.claims]: 'api/sitecore/RecentActivitiesPL/GetRecentPLClaimsActivities',
  [APIList.policies]: 'api/sitecore/RecentActivitiesPL/GetRecentPLPolicyActivities',
  [APIList.renewals]: 'api/sitecore/RecentActivitiesPL/GetRecentPLPolicyActivities',
};

type localTabDataHandlerProps = {
  api: APIList;
  body?: string;
};

export const useLocalTabDataHandler = (props: localTabDataHandlerProps) => {
  const { userData } = useUserData();
  const [data, setData] = useState<any>([]);
  const [isLoading, setLoading] = useState(true);
  const [error, setError] = useState<any>(null);

  useEffect(() => {
    const isDataExpired = (localStorageTimeStamp: string) => {
      const expiryTime = 4;
      const parsedTimeStamp = Date.parse(localStorageTimeStamp);
      const currentTime = new Date().getTime();
      const differenceInHours = Math.abs(parsedTimeStamp - currentTime) / 36e5;
      return differenceInHours > expiryTime;
    };

    const getDataFromSessionStorage = (key: string) =>
      new Promise((resolve, reject) => {
        const stringifiedObject = sessionStorage.getItem(key);
        if (stringifiedObject) {
          const sessionStoragePolicies = JSON.parse(stringifiedObject) as SessionStoragePolicies;
          if (isDataExpired(sessionStoragePolicies.timestamp)) reject();
          resolve(sessionStoragePolicies.data);
        } else reject();
      });

    const setDataInSessionStorage = (key: string, data: any) => {
      const sessionStoragePolicies: SessionStoragePolicies = {
        data,
        timestamp: new Date().toUTCString(),
      };
      sessionStorage.setItem(key, JSON.stringify(sessionStoragePolicies));
    };

    const fetchDataFromApi = async () => {
      return fetch(apis[props.api], {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: props.body,
      })
        .then((response) => response.json())
        .then((json) => {
          setData(json);
          return json;
        })
        .catch((e) => setError(e));
    };

    function getSessionStorageKey() {
      const input = (userData?.selectedStatCodeData?.FormattedSubStat ?? '') + (props.body ?? '');
      return 'ra-' + props.api + input;
    }

    const fetchData = () => {
      if (userData) {
        const cacheKey = getSessionStorageKey();
        getDataFromSessionStorage(cacheKey)
          .then((json) => setData(json))
          .catch(() => fetchDataFromApi().then((data) => setDataInSessionStorage(cacheKey, data)))
          .finally(() => setLoading(false));
      }
    };

    fetchData();
  }, [userData, props.api, props.body]);

  return { data, isLoading, error };
};

export default useLocalTabDataHandler;
